import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import utilityService from '../../services/utilityService';
import { useProfile } from '../../jetSetter/hooks';

const TwoFactorSetupPage = () => {
  const navigate = useNavigate();
  const { enableTwoFactor } = useProfile();

  const [authType, setAuthType] = useState('security-questions');
  const [setupStep, setSetupStep] = useState('select'); // select, setup, verify, success
  const [answers, setAnswers] = useState({
    question1: '',
    question2: '',
    question3: ''
  });
  const [otp, setOtp] = useState('');
  const [errors, setErrors] = useState({
    question1: '',
    question2: '',
    question3: '',
    otp: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [securityQuestions, setSecurityQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  // Fetch security questions when page loads or auth type changes
  useEffect(() => {
    if (authType === 'security-questions') {
      fetchSecurityQuestions();
    }
  }, [authType]);

  // Resend countdown timer
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  const fetchSecurityQuestions = async () => {
    try {
      setLoadingQuestions(true);
      const response = await utilityService.getSecurityQuestions();
      if (response.success && response.data) {
        setSecurityQuestions(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch security questions:', err);
      toast.error('Failed to load security questions');
    } finally {
      setLoadingQuestions(false);
    }
  };

  const handleAnswerChange = (questionKey, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionKey]: value
    }));
    if (errors[questionKey]) {
      setErrors(prev => ({
        ...prev,
        [questionKey]: ''
      }));
    }
  };

  const handleOtpChange = (value) => {
    setOtp(value.replace(/\D/g, '').slice(0, 6));
    if (errors.otp) {
      setErrors(prev => ({
        ...prev,
        otp: ''
      }));
    }
  };

  const handleAuthTypeChange = (type) => {
    setAuthType(type);
    setSetupStep('select');
    setOtp('');
    setAnswers({ question1: '', question2: '', question3: '' });
    setErrors({ question1: '', question2: '', question3: '', otp: '' });
  };

  const validateSecurityQuestions = () => {
    const newErrors = {
      question1: '',
      question2: '',
      question3: ''
    };

    if (!answers.question1.trim()) {
      newErrors.question1 = 'Please enter your answer to the security question.';
    }
    if (!answers.question2.trim()) {
      newErrors.question2 = 'Please enter your answer to the security question.';
    }
    if (!answers.question3.trim()) {
      newErrors.question3 = 'Please enter your answer to the security question.';
    }

    if (newErrors.question1 || newErrors.question2 || newErrors.question3) {
      setErrors(newErrors);
      return false;
    }
    return true;
  };

  const validateOtp = () => {
    if (!otp || otp.length !== 6) {
      setErrors(prev => ({
        ...prev,
        otp: 'Please enter a valid 6-digit code'
      }));
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (authType === 'security-questions') {
      if (!validateSecurityQuestions()) {
        return;
      }

      setIsLoading(true);
      try {
        const selectedQuestions = securityQuestions.slice(0, 3);

        await enableTwoFactor({
          method: 'security_questions',
          security_questions: [
            {
              question: selectedQuestions[0]?.question || 'Question 1',
              answer: answers.question1
            },
            {
              question: selectedQuestions[1]?.question || 'Question 2',
              answer: answers.question2
            },
            {
              question: selectedQuestions[2]?.question || 'Question 3',
              answer: answers.question3
            }
          ]
        });

        toast.success('Two-factor authentication enabled successfully');
        navigate('/jetsetter/profile/user-details');
      } catch (error) {
        console.error('Error saving security questions:', error);
        toast.error('Failed to save security questions: ' + (error.message || error));
      } finally {
        setIsLoading(false);
      }
    } else if (authType === 'email' || authType === 'sms') {
      if (setupStep === 'select') {
        setSetupStep('verify');
        toast.success(`Verification code sent via ${authType}`);
      } else if (setupStep === 'verify') {
        if (!validateOtp()) {
          return;
        }

        setIsLoading(true);
        try {
          await enableTwoFactor({
            method: authType,
            otp: otp
          });

          toast.success('Two-factor authentication enabled successfully');
          navigate('/jetsetter/profile/user-details');
        } catch (error) {
          console.error('Error verifying OTP:', error);
          toast.error('Failed to verify code: ' + (error.message || error));
        } finally {
          setIsLoading(false);
        }
      }
    }
  };

  const handleResendOtp = () => {
    if (resendCountdown === 0) {
      setResendCountdown(60);
      toast.success(`Verification code resent via ${authType}`);
    }
  };

  const handleCancel = () => {
    navigate('/jetsetter/profile/user-details');
  };

  const displayQuestions = securityQuestions.slice(0, 3);

  return (
    <div className="min-h-screen bg-gradient-to-br from-jet-gray-50 to-jet-gray-100 pt-[80px] md:pt-[90px] pb-12 px-3 sm:px-5">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-4">
          <h1 className="text-3xl md:text-4xl font-bold text-jet-gray-900 mb-2">
            Two-Factor Authentication Setup
          </h1>
          <p className="text-jet-gray-600">
            Secure your account with an additional layer of protection
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header Bar */}
          <div className="bg-jet-yellow px-6 py-4 border-b border-yellow-200">
            <h2 className="text-xl font-semibold text-jet-gray-900 m-0">
              Choose Authentication Method
            </h2>
          </div>

          {/* Content */}
          <div className="p-6 md:p-8">
            {/* Authentication Type Selection */}
            <div className="mb-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Email Option */}
                <label className="relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all duration-200"
                  style={{
                    borderColor: authType === 'email' ? '#ffd200' : '#d9d9d9',
                    backgroundColor: authType === 'email' ? '#fffbf0' : '#ffffff'
                  }}>
                  <input
                    type="radio"
                    name="authType"
                    value="email"
                    checked={authType === 'email'}
                    onChange={(e) => handleAuthTypeChange(e.target.value)}
                    className="w-4 h-4 cursor-pointer accent-[#ffd200]"
                  />
                  <div className="ml-3">
                    <p className="font-semibold text-jet-gray-900">Email</p>
                    <p className="text-sm text-jet-gray-600">Receive code via email</p>
                  </div>
                </label>

                {/* SMS Option */}
                <label className="relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all duration-200"
                  style={{
                    borderColor: authType === 'sms' ? '#ffd200' : '#d9d9d9',
                    backgroundColor: authType === 'sms' ? '#fffbf0' : '#ffffff'
                  }}>
                  <input
                    type="radio"
                    name="authType"
                    value="sms"
                    checked={authType === 'sms'}
                    onChange={(e) => handleAuthTypeChange(e.target.value)}
                    className="w-4 h-4 cursor-pointer accent-[#ffd200]"
                  />
                  <div className="ml-3">
                    <p className="font-semibold text-jet-gray-900">SMS</p>
                    <p className="text-sm text-jet-gray-600">Receive code via SMS</p>
                  </div>
                </label>

                {/* Security Questions Option */}
                <label className="relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all duration-200"
                  style={{
                    borderColor: authType === 'security-questions' ? '#ffd200' : '#d9d9d9',
                    backgroundColor: authType === 'security-questions' ? '#fffbf0' : '#ffffff'
                  }}>
                  <input
                    type="radio"
                    name="authType"
                    value="security-questions"
                    checked={authType === 'security-questions'}
                    onChange={(e) => handleAuthTypeChange(e.target.value)}
                    className="w-4 h-4 cursor-pointer accent-[#ffd200]"
                  />
                  <div className="ml-3">
                    <p className="font-semibold text-jet-gray-900">Security Questions</p>
                    <p className="text-sm text-jet-gray-600">Answer personal questions</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Security Questions Section */}
            {authType === 'security-questions' && (
              <div className="space-y-6 mb-2">
                {loadingQuestions ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#ffd200]"></div>
                    <p className="text-jet-gray-600 text-sm mt-3">Loading security questions...</p>
                  </div>
                ) : (
                  <>
                    {displayQuestions.map((q, idx) => (
                      <div key={idx} className="flex flex-col">
                        <label className="block text-sm font-semibold text-jet-gray-900 mb-2">
                          Question {idx + 1}: {q.question || `Question ${idx + 1}`}
                        </label>
                        <input
                          type="text"
                          className="w-full px-4 py-3 border border-jet-gray-300 rounded-lg text-jet-gray-900 font-medium text-sm bg-white transition-colors duration-200 focus:outline-none focus:border-jet-yellow focus:shadow-[0_0_0_2px_rgba(255,215,0,0.2)]"
                          placeholder="Your answer"
                          value={answers[`question${idx + 1}`] || ''}
                          onChange={(e) => handleAnswerChange(`question${idx + 1}`, e.target.value)}
                        />
                        {errors[`question${idx + 1}`] && (
                          <p className="text-red-600 text-xs mt-1">{errors[`question${idx + 1}`]}</p>
                        )}
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}

            {/* Buttons */}
            <div className="flex gap-3 justify-end border-t border-jet-gray-200 pt-6">
              <button
                onClick={handleCancel}
                disabled={isLoading}
                className="px-6 py-3 bg-jet-gray-200 text-jet-gray-800 rounded-lg font-semibold text-sm hover:bg-jet-gray-300 transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isLoading}
                className="px-6 py-3 bg-gradient-to-br from-jet-yellow to-jet-yellow-hover text-jet-gray-900 rounded-lg font-semibold text-sm hover:shadow-lg transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Processing...' : (setupStep === 'verify' ? 'Verify & Enable' : 'Continue')}
              </button>
            </div>
          </div>
        </div>

        {/* Info Box */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-blue-900 text-sm">
            <strong>Why 2FA?</strong> Two-factor authentication adds an extra layer of security to your account.
            Even if someone has your password, they won't be able to access your account without the second verification method.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorSetupPage;
